import React, { useEffect, useState } from "react";

import { Button, makeStyles } from "@material-ui/core";

import { useHistory } from "react-router-dom";

import UpdateSubscriptionTable from "./UpdateSubscriptionTable";

import IncreaseSubscriptionModal from "../../../../components/Modals/IncreaseSubscriptionModal/IncreaseSubscriptionModal";
import DecreaseSubscriptionModal from "../../../../components/Modals/DecreaseSubscriptionModal/DecreaseSubscriptionModal";
import ApproveModal from "../../../../components/Modals/ApproveModal/ApproveModal";
import SubscriptionCreatingErrorModal from "../../../../components/Modals/SubscriptionCreatingErrorModal/SubscriptionCreatingErrorModal";
import StripeQuantityModal from "../../../../components/Modals/StripeQuantityModal/StripeQuantityModal";
import ErrorModal from "../../../../components/Modals/ErrorModal/ErrorModal";
import { apiErrorData, apiErrorMessage } from "../../../../helpers/apiErrorMessage";
import { paypalTierChangeMessage } from "../../../../helpers/paypalTierChangeMessage";

const useStyles = makeStyles({
  button: {
    maxWidth: 220,
    display: "flex",
    margin: "30px auto",
  },
  backButtonOuter: {
    display: "flex",
    justifyContent: "center",
  },
  backButton: {
    marginTop: 100,
  },
});

const UpdateSubscription = ({
  subscriptionQuantity,
  nextCycleQuantity,
  subscriptionSource,
  subscriptionTiers,
  email,
  existingHostsQuantity,
  updateAdminSubscription,
  previewStripeQuantity,
  getPrice,
  back,
  onUpdated,
  goToHostsList,
}) => {
  const isStripe = subscriptionSource === "stripe";
  const [newSubscriptionQuantity, setNewSubscriptionQuantity] = useState(
    +subscriptionQuantity
  );
  const [subscriptionFee, setSubscriptionFee] = useState(0);
  const [openIncSubModal, setOpenIncSubModal] = useState(false);
  const [openDecSubModal, setOpenDecSubModal] = useState(false);
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openSubscriptionErrorModal, setOpenSubscriptionErrorModal] =
    useState(false);
  const [paypalError, setPaypalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [approveLink, setApproveLink] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [subscriptionUpdatingError, setSubscriptionUpdatingError] =
    useState("");
  const [errorActionHref, setErrorActionHref] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const history = useHistory();

  const classes = useStyles();

  const decreaseHostsQuantity = () => {
    if (newSubscriptionQuantity <= 3) {
      setQuantityError("Minimum hosts quantity is 3.");
      return;
    }
    setNewSubscriptionQuantity(newSubscriptionQuantity - 1);
  };

  const increaseHostsQuantity = () => {
    setNewSubscriptionQuantity(newSubscriptionQuantity + 1);
  };

  const handleClose = () => {
    if (loading) return;
    setOpenIncSubModal(false);
    setOpenDecSubModal(false);
    setOpenStripeModal(false);
    setOpenApproveModal(false);
    setSubscriptionUpdatingError("");
    setErrorActionHref("");
    setOpenSubscriptionErrorModal(false);
    setPaypalError("");
    setPreview(null);
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            currency_code: "USD",
            value: Number(subscriptionFee).toFixed(2),
          },
          custom_id: email,
        },
      ],
    });
  };

  const paypalOnError = (err) => {
    const message = apiErrorMessage(
      err,
      "PayPal couldn't start this payment. Close this window and try again."
    );
    setPaypalError(message);
    setOpenSubscriptionErrorModal(true);
  };

  const paypalOnApprove = (data) => {
    setApproved(true);
    updateSubscription(data.orderID, newSubscriptionQuantity);
  };

  const updateSubscription = async (orderID, quantity) => {
    try {
      setLoading(true);
      const response = await updateAdminSubscription(orderID, quantity);

      if (response.provider === "stripe" || isStripe) {
        setOpenStripeModal(false);
        if (onUpdated) onUpdated(response.message);
        return;
      }

      setOpenIncSubModal(false);
      setOpenDecSubModal(false);
      setOpenApproveModal(true);
      setApproveLink(response.link);
    } catch (err) {
      const portalUrl = apiErrorData(err)?.portalUrl;
      setErrorActionHref(portalUrl || "");
      setSubscriptionUpdatingError(
        apiErrorMessage(
          err,
          "Couldn't update the subscription. Try again in a moment."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const openUpdateSubscriptionModal = () => {
    if (quantityError) return;
    if (isStripe) {
      setOpenStripeModal(true);
      return;
    }
    if (newSubscriptionQuantity > subscriptionQuantity) {
      setOpenIncSubModal(true);
    } else if (newSubscriptionQuantity < subscriptionQuantity) {
      setOpenDecSubModal(true);
    } else if (nextCycleQuantity && +nextCycleQuantity !== +subscriptionQuantity) {
      setSubscriptionUpdatingError(
        "PayPal host decreases are finished from the approval link. This page can't cancel a PayPal change that's already waiting for approval."
      );
    }
  };

  useEffect(() => {
    if (newSubscriptionQuantity > subscriptionQuantity) {
      const calculatedFee =
        getPrice(newSubscriptionQuantity) - getPrice(subscriptionQuantity);
      setSubscriptionFee(calculatedFee);
      return;
    }
    setSubscriptionFee(0);
  }, [subscriptionQuantity, newSubscriptionQuantity, getPrice]);

  useEffect(() => {
    if (newSubscriptionQuantity < 3) {
      setQuantityError("Minimum hosts quantity is 3.");
      return;
    }
    if (newSubscriptionQuantity < existingHostsQuantity) {
      const extra = existingHostsQuantity - newSubscriptionQuantity;
      setQuantityError(
        `You have ${existingHostsQuantity} hosts. Remove ${extra} before lowering the subscription to ${newSubscriptionQuantity}.`
      );
      return;
    }
    if (!isStripe) {
      const tierError = paypalTierChangeMessage(
        subscriptionTiers,
        subscriptionQuantity,
        newSubscriptionQuantity
      );
      if (tierError) {
        setQuantityError(tierError);
        return;
      }
    }
    setQuantityError("");
  }, [
    existingHostsQuantity,
    isStripe,
    newSubscriptionQuantity,
    subscriptionQuantity,
    subscriptionTiers,
  ]);

  useEffect(() => {
    if (!openStripeModal) return undefined;
    let cancelled = false;
    setPreview(null);
    setPreviewLoading(true);
    previewStripeQuantity(newSubscriptionQuantity)
      .then((response) => {
        if (!cancelled) setPreview(response);
      })
      .catch((err) => {
        if (!cancelled) {
          const status = err?.response?.status;
          setPreview({
            blocked: status >= 400 && status < 500,
            portalUrl: apiErrorData(err)?.portalUrl || "",
            change: newSubscriptionQuantity > subscriptionQuantity ? "increase" : "decrease",
            message: apiErrorMessage(
              err,
              "Couldn't preview the charge. You can still confirm, and Stripe will bill the prorated difference if this adds hosts."
            ),
          });
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    openStripeModal,
    newSubscriptionQuantity,
    previewStripeQuantity,
    subscriptionQuantity,
  ]);

  const quantityChanged =
    +subscriptionQuantity !== newSubscriptionQuantity ||
    (+nextCycleQuantity && +nextCycleQuantity !== newSubscriptionQuantity);
  const feeLabel = isStripe ? "Due today" : "PayPal amount";
  const feeValue = isStripe
    ? newSubscriptionQuantity > subscriptionQuantity
      ? preview?.dueToday == null
        ? "Prorated"
        : `$${preview.dueToday}`
      : "$0"
    : `$${subscriptionFee}`;

  return (
    <>
      <h1>Update Subscription</h1>
      <p className="subscription-header-subtitle" style={{ textAlign: "center", marginBottom: 8 }}>
        {isStripe
          ? "This subscription is billed to the card on file. Adding a host charges a prorated amount today. Removing a host starts on the next renewal."
          : "This subscription is billed with PayPal. Adding a host asks PayPal to approve the new quantity. PayPal can't move you across a pricing tier."}
      </p>

      <UpdateSubscriptionTable
        error={quantityError}
        subscriptionQuantity={subscriptionQuantity}
        newSubscriptionQuantity={newSubscriptionQuantity}
        newMonthlyPrice={getPrice(newSubscriptionQuantity)}
        feeLabel={feeLabel}
        feeValue={feeValue}
        decrease={decreaseHostsQuantity}
        increase={increaseHostsQuantity}
      />

      {quantityChanged &&
        newSubscriptionQuantity >= existingHostsQuantity &&
        !quantityError && (
          <Button
            variant="contained"
            onClick={openUpdateSubscriptionModal}
            color="primary"
            className={classes.button}
          >
            Update subscription
          </Button>
        )}

      {newSubscriptionQuantity < existingHostsQuantity && (
        <Button
          variant="contained"
          onClick={goToHostsList}
          color="primary"
          className={classes.button}
        >
          Go to hosts list
        </Button>
      )}

      <div className={classes.backButtonOuter}>
        <Button
          className={classes.backButton}
          variant="contained"
          color="default"
          onClick={back}
        >
          Go back
        </Button>
      </div>

      <StripeQuantityModal
        open={openStripeModal}
        loading={loading}
        previewLoading={previewLoading}
        preview={preview}
        hostsQuantity={newSubscriptionQuantity}
        monthlyPrice={getPrice(newSubscriptionQuantity)}
        onConfirm={() => updateSubscription(null, newSubscriptionQuantity)}
        handleClose={handleClose}
      />

      <IncreaseSubscriptionModal
        open={openIncSubModal}
        hostsQuantity={newSubscriptionQuantity}
        fee={subscriptionFee}
        loading={loading}
        approved={approved}
        price={getPrice(newSubscriptionQuantity)}
        createOrder={createOrder}
        handleClose={handleClose}
        onError={paypalOnError}
        catchError={paypalOnError}
        onApprove={paypalOnApprove}
      />

      <DecreaseSubscriptionModal
        open={openDecSubModal}
        hostsQuantity={newSubscriptionQuantity}
        loading={loading}
        update={() => updateSubscription(null, newSubscriptionQuantity)}
        price={getPrice(newSubscriptionQuantity)}
        handleClose={handleClose}
      />

      <ApproveModal
        open={openApproveModal}
        approve={() => history.push(approveLink)}
        link={approveLink}
      />

      <SubscriptionCreatingErrorModal
        open={openSubscriptionErrorModal}
        message={paypalError}
        handleClose={handleClose}
      />

      <ErrorModal
        open={!!subscriptionUpdatingError}
        title="Couldn't update the subscription"
        errorText={subscriptionUpdatingError}
        actionHref={errorActionHref}
        actionLabel="Update card"
        handleClose={handleClose}
      />
    </>
  );
};

export default UpdateSubscription;
